import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { ForecastResult } from '../services/forecastService';
import { TrendDataPoint } from '../services/historicalAnalysisService';
import { ScaleFormat } from '@/types/base';
import { InfoRibbon } from '../../InfoRibbon';
import { describeTrendGranularity } from '../utils/trendAggregation';

interface ForecastVisualizationProps {
  forecast: ForecastResult;
  historicalData: TrendDataPoint[];
  satisfactionScale: ScaleFormat;
  loyaltyScale: ScaleFormat;
  satisfactionLabel?: string;
  loyaltyLabel?: string;
}

export const ForecastVisualization: React.FC<ForecastVisualizationProps> = ({
  forecast,
  historicalData,
  satisfactionScale,
  loyaltyScale,
  satisfactionLabel = 'Satisfaction',
  loyaltyLabel = 'Loyalty'
}) => {
  const labels = { satisfaction: satisfactionLabel, loyalty: loyaltyLabel };
  const granularity = historicalData[0]?.granularity || 'day';
  const granularityLabel = describeTrendGranularity(granularity);

  // Prepare data for charts - mark forecast points
  const historicalChartData = useMemo(() => historicalData.map(point => ({
    date: point.date,
    satisfaction: point.averageSatisfaction,
    loyalty: point.averageLoyalty,
    isForecast: false
  })), [historicalData]);

  const forecastChartData = useMemo(() => forecast.satisfactionForecast.map((point) => ({
    date: point.date,
    satisfaction: point.forecastedSatisfaction,
    loyalty: point.forecastedLoyalty,
    confidence: point.confidence,
    isForecast: true
  })), [forecast.satisfactionForecast]);

  // Combine data - add last historical point to connect to forecast smoothly
  const lastHistorical = historicalChartData[historicalChartData.length - 1];
  const combinedData = useMemo(() => [
    ...historicalChartData,
    ...(lastHistorical ? [{
      ...lastHistorical,
      date: lastHistorical.date
    }] : []),
    ...forecastChartData
  ], [historicalChartData, lastHistorical, forecastChartData]);

  // Aggregation already caps history; hide historical dots if still dense
  const showHistoricalDots = historicalChartData.length <= 36;

  // Determine Y-axis domain
  const getYAxisDomain = (scale: ScaleFormat): [number, number] => {
    const max = parseInt(scale.split('-')[1]);
    return [0, max];
  };

  const [satYMin, satYMax] = getYAxisDomain(satisfactionScale);
  const [loyYMin, loyYMax] = getYAxisDomain(loyaltyScale);

  const getConfidenceColor = (confidence: 'high' | 'medium' | 'low'): string => {
    switch (confidence) {
      case 'high': return '#10b981';
      case 'medium': return '#f59e0b';
      case 'low': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const avgConfidence = forecast.satisfactionForecast.length > 0
    ? forecast.satisfactionForecast[0].confidence
    : 'low';

  const renderDot = (color: string) => (props: any) => {
    const isForecast = props.payload?.isForecast;
    if (!isForecast && !showHistoricalDots) {
      return <g />;
    }
    return (
      <circle
        cx={props.cx}
        cy={props.cy}
        r={isForecast ? 5 : 3}
        fill={color}
        stroke={isForecast ? '#f59e0b' : color}
        strokeWidth={isForecast ? 2 : 1}
      />
    );
  };

  const groupingNote = granularity === 'day'
    ? 'Forecast based on linear trend projection from daily averages.'
    : `Forecast based on linear trend projection from ${granularityLabel} averages (dates were grouped to keep charts readable).`;

  return (
    <div className="forecast-visualization">
      <div className="forecast-header">
        <h4 className="forecast-title">Forecast: If Trend Continues</h4>
        <InfoRibbon text={`${groupingNote} Actual results may vary.`} />
      </div>

      <div className="forecast-charts">
        <div className="forecast-chart-item">
          <h5 className="forecast-chart-title">{labels.satisfaction} Forecast</h5>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={combinedData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="date" 
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis 
                domain={[satYMin, satYMax]}
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '12px'
                }}
                formatter={(value: number, name: string, props: any) => {
                  const isForecast = props.payload?.isForecast;
                  return [
                    value.toFixed(2), 
                    isForecast ? `${labels.satisfaction} (Forecast)` : `${labels.satisfaction} (Historical)`
                  ];
                }}
              />
              <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
              {lastHistorical && (
                <ReferenceLine 
                  x={lastHistorical.date} 
                  stroke="#f59e0b" 
                  strokeDasharray="3 3"
                  label={{ value: "Forecast", position: "top", fill: "#f59e0b", fontSize: 10 }}
                />
              )}
              <Line 
                type="monotone" 
                dataKey="satisfaction" 
                stroke="#3a863e" 
                strokeWidth={2}
                dot={renderDot('#3a863e')}
                name={labels.satisfaction}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
          <div className="forecast-metrics">
            <span className="forecast-metric">
              Trend: {forecast.trendSlope.satisfaction > 0 ? '+' : ''}
              {forecast.trendSlope.satisfaction.toFixed(3)} per day
            </span>
            <span className="forecast-metric">
              R²: {(forecast.rSquared.satisfaction * 100).toFixed(1)}%
            </span>
            <span 
              className="forecast-confidence"
              style={{ color: getConfidenceColor(avgConfidence) }}
            >
              Confidence: {avgConfidence}
            </span>
          </div>
        </div>

        <div className="forecast-chart-item">
          <h5 className="forecast-chart-title">{labels.loyalty} Forecast</h5>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={combinedData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="date" 
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis 
                domain={[loyYMin, loyYMax]}
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '12px'
                }}
                formatter={(value: number, name: string, props: any) => {
                  const isForecast = props.payload?.isForecast;
                  return [
                    value.toFixed(2), 
                    isForecast ? `${labels.loyalty} (Forecast)` : `${labels.loyalty} (Historical)`
                  ];
                }}
              />
              <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
              {lastHistorical && (
                <ReferenceLine 
                  x={lastHistorical.date} 
                  stroke="#f59e0b" 
                  strokeDasharray="3 3"
                  label={{ value: "Forecast", position: "top", fill: "#f59e0b", fontSize: 10 }}
                />
              )}
              <Line 
                type="monotone" 
                dataKey="loyalty" 
                stroke="#4682B4" 
                strokeWidth={2}
                dot={renderDot('#4682B4')}
                name={labels.loyalty}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
          <div className="forecast-metrics">
            <span className="forecast-metric">
              Trend: {forecast.trendSlope.loyalty > 0 ? '+' : ''}
              {forecast.trendSlope.loyalty.toFixed(3)} per day
            </span>
            <span className="forecast-metric">
              R²: {(forecast.rSquared.loyalty * 100).toFixed(1)}%
            </span>
            <span 
              className="forecast-confidence"
              style={{ color: getConfidenceColor(avgConfidence) }}
            >
              Confidence: {avgConfidence}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
